import { useCallback } from 'react'

import moment from 'moment'
import { useSelector } from 'react-redux'

import { DeliveryPoint } from '@library/api-postcode-lookup'

import { fetchDeliveryDays } from 'apis/deliveries'
import { getBasketDate } from 'selectors/basket'
import { getNDDFeatureValue } from 'selectors/features'
import {
  getAvailableDeliveryDays,
  getDeliveryTariffId,
  getNDDFeatureFlagVal,
  transformDaySlotLeadTimesToMockSlots,
} from 'utils/deliveries'
import logger from 'utils/logger'

export const useCheckCanDeliver = (
  setPostcodeError: (error: string) => void,
  setDeliveryPoints: (points: DeliveryPoint[]) => void,
  postcode: string,
) => {
  const deliveryDate = useSelector(getBasketDate)
  const menuCutoffUntil = useSelector((state) => (state as any).menuCutoffUntil)
  const isNDDExperiment = useSelector(getNDDFeatureFlagVal)
  const deliveryTariffId = useSelector((state) =>
    getDeliveryTariffId((state as any).user, getNDDFeatureValue(state as any)),
  )

  const checkCanDeliver = useCallback(async () => {
    if (!deliveryDate) {
      setPostcodeError("Sorry, we don't deliver to this postcode yet")
      setDeliveryPoints([])

      return
    }

    const cutOfFrom = moment().startOf('day').toISOString()
    const menuCutoffUntilFallback = moment().startOf('day').add(30, 'days').toISOString()
    const cutOfUntil = menuCutoffUntil || menuCutoffUntilFallback

    try {
      let { data: days } = await fetchDeliveryDays(
        null,
        cutOfFrom,
        cutOfUntil,
        isNDDExperiment,
        deliveryTariffId,
        postcode,
      )

      if (isNDDExperiment) {
        days = transformDaySlotLeadTimesToMockSlots(days)
      }

      const availableDeliveryDays = getAvailableDeliveryDays(days)

      if (
        availableDeliveryDays?.[deliveryDate] &&
        !availableDeliveryDays[deliveryDate].alternateDeliveryDay
      ) {
        setPostcodeError('')

        return
      }
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      logger.error(error)
    }

    setPostcodeError("Sorry, we don't deliver to this postcode yet")
    setDeliveryPoints([])
  }, [
    deliveryDate,
    deliveryTariffId,
    isNDDExperiment,
    menuCutoffUntil,
    postcode,
    setDeliveryPoints,
    setPostcodeError,
  ])

  return { checkCanDeliver }
}
